import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import Breadcrumb from "../../DefaultComponent/BreadCrumb";
import Spinner from "../../DefaultComponent/Spinner";
import ToastDefault from "../../DefaultComponent/Toast";
import { calculateTotalPages } from "../../../utils/Helper";
import {
  CurrencyCreditRateListAction,
  CurrencyCreditRateCreateAction,
  CurrencyCreditRateUpdateAction,
  CurrencyCreditRateDeleteAction,
} from "../../../Redux/Actions/PricingAction";
import {
  PricingPageHeader,
  StatusChip,
  pricingBodyRowSx,
  pricingTableContainerSx,
  pricingTableHeadSx,
} from "../shared/pricingHelpers";
import CurrencyCreditRatesForm, {
  CurrencyCreditRateValues,
} from "./CurrencyCreditRatesForm/CurrencyCreditRatesForm";

const ROWS_PER_PAGE = 10;

const CurrencyCreditRates: React.FC = () => {
  const dispatch = useDispatch<any>();
  const { rows = [], loading } = useSelector(
    (state: any) => state.CurrencyCreditRateListData || {}
  );

  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [editing, setEditing] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchList = () => dispatch(CurrencyCreditRateListAction());

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paged = useMemo(
    () => rows.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE),
    [rows, page]
  );

  const openCreate = () => {
    setMode("create");
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row: any) => {
    setMode("edit");
    setEditing(row);
    setFormOpen(true);
  };

  const initialFromRow = (row: any): Partial<CurrencyCreditRateValues> | null =>
    row
      ? {
          currency: row.currency ?? "USD",
          cost_per_credit: row.cost_per_credit ?? "",
          is_active: row.is_active ?? true,
        }
      : null;

  const handleSubmit = async (payload: any) => {
    setSaving(true);
    const res =
      mode === "create"
        ? await dispatch(CurrencyCreditRateCreateAction(payload))
        : await dispatch(CurrencyCreditRateUpdateAction(editing.id, payload));
    setSaving(false);
    // On a 400 (e.g. duplicate active currency) the helper toasts the server
    // message and res.status is false — keep the dialog open so the user can fix.
    if (res?.status === true) {
      setFormOpen(false);
      fetchList();
    }
  };

  const handleDelete = (row: any) => {
    Swal.fire({
      title: "Delete rate?",
      text: `Credit rate for ${row.currency} will be removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d92d20",
      cancelButtonColor: "#667085",
      confirmButtonText: "Delete",
    }).then(async (r) => {
      if (r.isConfirmed) {
        const res = await dispatch(CurrencyCreditRateDeleteAction(row.id));
        if (res?.status === true) fetchList();
      }
    });
  };

  return (
    <>
      {loading && <Spinner />}
      <div
        className="page-wrapper card"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div className="col-md-12">
          <Breadcrumb
            pageTitle="Currency Credit Rates"
            breadcrumbs={[
              { label: "Pricing", path: "/pricing/currency-credit-rates", isActive: false },
              { label: "Currency Credit Rates", path: "/pricing/currency-credit-rates", isActive: true },
            ]}
          />
        </div>

        <PricingPageHeader
          title="Currency Credit Rates"
          subtitle="Money value of one credit per currency (only one active rate per currency)."
          actionLabel="Add Rate"
          onAction={openCreate}
        />

        <Box sx={{ mx: 2, mb: 2 }}>
          <TableContainer component={Paper} sx={pricingTableContainerSx}>
            <Table aria-label="Currency Credit Rates">
              <TableHead sx={pricingTableHeadSx}>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell align="right">Cost per Credit</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length > 0 ? (
                  paged.map((row: any, idx: number) => (
                    <TableRow key={row.id} sx={pricingBodyRowSx}>
                      <TableCell>{(page - 1) * ROWS_PER_PAGE + idx + 1}</TableCell>
                      <TableCell>{row.currency}</TableCell>
                      <TableCell align="right">{row.cost_per_credit}</TableCell>
                      <TableCell align="center">
                        <StatusChip active={Boolean(row.is_active)} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row)}
                            sx={{ color: "#d92d20" }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 5, color: "#667085" }}>
                      No credit rates found. Click “Add Rate” to create one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {rows.length > ROWS_PER_PAGE && (
          <div className="pagination-style">
            <Stack spacing={2}>
              <Pagination
                count={calculateTotalPages(rows.length, ROWS_PER_PAGE)}
                page={page}
                onChange={(_e, p) => setPage(p)}
                color="primary"
                variant="outlined"
                shape="rounded"
              />
            </Stack>
          </div>
        )}
      </div>

      <CurrencyCreditRatesForm
        open={formOpen}
        mode={mode}
        initial={initialFromRow(editing)}
        saving={saving}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />
      <ToastDefault />
    </>
  );
};

export default CurrencyCreditRates;
